import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';

import { JobSurge, paySurgeClient, surgeErrorMessage } from '../api';
import {
  dollarsToCents,
  formatCents,
  sanitizeAmountInput,
  surgeHolidayNote,
  surgeScopeNote,
} from '../format';

import SurgeDialog from './SurgeDialog';

type Props = {
  surge: JobSurge;
  onSaved: (surge: JobSurge) => void;
  onClose: () => void;
};

/**
 * Override surge pay amount (GRAVY-10443; Figma 38500:97200). The current rate
 * is read-only; the override is a new amount above base pay.
 *
 * No reason is asked for: Product amended AC 8 — the source is captured when a
 * worker is actually hired. The subtitle carries the agreed copy fix (the
 * frame's comma splice and trailing comma).
 *
 * Submit stays disabled until the amount is a real raise within the ceiling.
 * The server re-checks both, and its refusal is shown as it words it.
 */
const OverrideSurgeModal = ({ surge, onSaved, onClose }: Props) => {
  const [amount, setAmount] = useState('');
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const cents = dollarsToCents(amount);
  const localError = !amount.trim()
    ? null
    : cents == null || cents <= 0
      ? 'Enter a dollar amount like 2.50.'
      : cents > surge.max_cents
        ? `Surge pay can’t be more than ${formatCents(surge.max_cents)}/hr above base.`
        : cents <= surge.applied_cents
          ? `Enter more than the current ${formatCents(surge.applied_cents)}/hr.`
          : null;
  const canSubmit = cents != null && !localError && !saving;

  const submit = async () => {
    if (cents == null) return;
    setSaving(true);
    setServerError(null);
    try {
      onSaved(await paySurgeClient.overrideJobSurge(surge.job_id, cents));
      onClose();
    } catch (e) {
      setServerError(
        surgeErrorMessage(e) ??
          'Could not override surge pay. Please try again.',
      );
    } finally {
      setSaving(false);
    }
  };

  const error = localError ?? serverError;

  return (
    <SurgeDialog
      testId="overrideSurge"
      title="Override surge pay amount"
      subtitle={surgeScopeNote(surge.shifts_count)}
      onClose={onClose}
    >
      <div className="paysurge-stack">
        <label className="paysurge-field">
          <span className="paysurge-label">Current surge rate</span>
          <input
            className="paysurge-readonly"
            readOnly
            value={formatCents(surge.applied_cents)}
          />
        </label>
        <label className="paysurge-field">
          <span className="paysurge-label">Override amount</span>
          <input
            className={`paysurge-input${error ? ' has-error' : ''}`}
            aria-invalid={Boolean(error)}
            data-testid="overrideAmount"
            inputMode="decimal"
            placeholder="$0.00"
            value={amount ? `$${amount}` : ''}
            onChange={(e) => {
              setServerError(null);
              setAmount(sanitizeAmountInput(e.target.value));
            }}
          />
          {error && (
            <span className="paysurge-fielderror" role="alert">
              {error}
            </span>
          )}
        </label>
        {/* GRAVY-10882 — the amount is added before the holiday multiplier,
            so say so; with a valid amount typed, name it. */}
        {surge.is_holiday && (
          <p className="paysurge-info" data-testid="overrideHolidayNote">
            <InformationCircleIcon aria-hidden="true" />
            {cents != null && cents > 0 && !localError
              ? surgeHolidayNote(cents, 'coming')
              : 'The override amount will be applied on top of the holiday rate.'}
          </p>
        )}
      </div>
      <div className="paysurge-actions">
        <button
          type="button"
          className="paysurge-btn is-outline"
          onClick={onClose}
        >
          Cancel
        </button>
        <button
          type="button"
          className="paysurge-btn is-primary"
          data-testid="overrideSubmit"
          disabled={!canSubmit}
          onClick={submit}
        >
          {saving ? 'Submitting…' : 'Submit'}
        </button>
      </div>
    </SurgeDialog>
  );
};

export default OverrideSurgeModal;
